import { GridColDef, GridFilterModel, GridSortModel, GridValidRowModel } from '@mui/x-data-grid-pro';
import { logError } from 'setup/logger/logError';
import { DataGridInstanceName } from 'services/storage/keys';
import { LocalStorageManager } from 'services/storage/LocalStorageManager';
import { isGridFilterModel } from '../filterGuards';

export { isGridFilterModel } from '../filterGuards';


export const CLIENT_SORTING_MODE = 'client' as const;
export const CLIENT_FILTER_MODE = 'client' as const;

export const orderColumnsBySavedOrder = <T extends GridValidRowModel>(
  columns: GridColDef<T>[],
  savedOrderedFields?: string[]
): GridColDef<T>[] => {
  if (!savedOrderedFields?.length) {
    return columns;
  }

  const columnsByField = new Map(
    columns.map((column) => [column.field, column])
  );

  const ordered = savedOrderedFields
    .map((field) => columnsByField.get(field))
    .filter(Boolean) as GridColDef<T>[];

  const orderedFieldSet = new Set(ordered.map((column) => column.field));
  const remaining = columns.filter(
    (column) => !orderedFieldSet.has(column.field)
  );

  return [...ordered, ...remaining];
};

type SavedColumnDimension = {
  width?: number;
  minWidth?: number;
  maxWidth?: number;
};

export const applySavedColumnDimensions = <T extends GridValidRowModel>(
  columns: GridColDef<T>[],
  savedDimensions?: Record<string, SavedColumnDimension>
): GridColDef<T>[] => {
  if (!savedDimensions) {
    return columns;
  }

  return columns.map((column) => {
    const dimension = savedDimensions[column.field];

    if (!dimension || typeof dimension.width !== 'number') {
      return column;
    }

    return {
      ...column,
      width: dimension.width,
      ...(typeof dimension.minWidth === 'number' && {
        minWidth: dimension.minWidth,
      }),
      ...(typeof dimension.maxWidth === 'number' && {
        maxWidth: dimension.maxWidth,
      }),
      flex: undefined,
    };
  });
};

/**
 * getRowId function for rows with _id field
 * Used instead of inline functions to maintain stable reference
 */
export const getRowIdByIdField = <T extends { _id: string }>(
  row: T
): string => {
  return row._id;
};

/**
 * Helper function to read initial filterModel and sortModel from localStorage
 * Compatible with the format used by useDataGridStateStorage hook (DataGrid exportState format)
 *
 * @returns Object with filterModel and sortModel, or defaults if not found
 */
export function getInitialStateFromStorage(
  dataGridInstanceName: DataGridInstanceName
): {
  filterModel: GridFilterModel;
  sortModel: GridSortModel;
} {
  const defaultFilterModel: GridFilterModel = { items: [] };
  const defaultSortModel: GridSortModel = [];

  try {
    const savedState = LocalStorageManager.getDataGridState(dataGridInstanceName);
    if (!savedState) {
      return {
        filterModel: defaultFilterModel,
        sortModel: defaultSortModel,
      };
    }

    const filterModel = isGridFilterModel(savedState?.filter?.filterModel)
      ? savedState.filter.filterModel
      : defaultFilterModel;

    const sortModel = isValidSortModel(savedState?.sorting?.sortModel)
      ? savedState.sorting.sortModel
      : defaultSortModel;

    return { filterModel, sortModel };
  } catch (error) {
    logError('Failed to parse initial state from localStorage', error);
    return {
      filterModel: defaultFilterModel,
      sortModel: defaultSortModel,
    };
  }
}

function isValidSortModel(value: unknown): value is GridSortModel {
  return Array.isArray(value);
}
